import React, { useMemo, useState } from "react"

import { CRITERION_VALUE } from "@/constants/criterion-values"
import { selectPrimaryReviewerMerged } from "@/human-feedback/annotations"
import {
  useAnnotationsWithMetricOutputsTransport,
  useDeleteReviewAnnotationTransport,
  useHumanReviewProjectsTransport,
  useReviewMetricTransportInvalidation,
  useUpdateReviewAnnotationTransport,
} from "@/human-feedback/transport"
import { MetricVersionTag } from "@/metrics/metric-display"
import { useSimulationOutputTransport } from "@/runs"
import { toast, Tooltip } from "@coval/ui"
import { RotateCw } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { Message } from "@/types/Transcript"

import { isValidCriterionResult, type CriterionResult } from "../../types"
import {
  ALL_PROJECTS_FILTER,
  canRevealHumanVsAiAgreement,
  formatDate,
  getAnnotationProjectIds,
} from "../../utils/filterSimulations"
import { useTestDrawer, useTestMetricRuntime } from "../contexts"
import { useCanRunEvaluations } from "../hooks/useCanRunEvaluations"
import { extractCompositeResults } from "../utils/compositeResults"
import { CompositeReviewSection } from "./CompositeReviewSection"
import { HumanReviewAnnotationCard } from "./HumanReviewAnnotationCard"
import { ReviewBadge } from "./ReviewBadge"
import { TranscriptDetailView } from "./TranscriptDetailView"

const log = logger("SimulationDetailView")

interface SimulationDetailViewProps {
  currentIndex: number
  totalCount: number
  onNext: () => void
  onPrevious: () => void
  experimentResult?: {
    value: string | null
    explanation: string
    errorStatus?: string
    errorMessage?: string
    criteriaResults?: CriterionResult[]
  } | null
  onRunExperiment: () => void
  isRunningExperiment: boolean
  selectedProjectId?: string
  bodyOverlay?: React.ReactNode
}

export const SimulationDetailView: React.FC<SimulationDetailViewProps> = ({
  currentIndex,
  totalCount,
  onNext,
  onPrevious,
  experimentResult,
  onRunExperiment,
  isRunningExperiment,
  selectedProjectId = ALL_PROJECTS_FILTER,
  bodyOverlay,
}) => {
  const { metric, criteriaFirst, criteriaOverride, criteriaViewMode } =
    useTestMetricRuntime()
  const { trackEvent } = useAnalytics()
  const canRunEvaluations = useCanRunEvaluations()
  const isCompositeMetric =
    metric?.metric_manager === "manager_composite_evaluation"
  const { selectedSimulationId } = useTestDrawer()
  const simulationId = selectedSimulationId!

  const invalidateReviewMetricTransport = useReviewMetricTransportInvalidation()

  // Fetch annotations within the exact selected project when one is chosen.
  const annotationProjectIds = useMemo(
    () => getAnnotationProjectIds(selectedProjectId),
    [selectedProjectId],
  )
  const {
    data: annotationsData,
    isLoading: isInitialLoadingAnnotations,
    isFetching: isFetchingAnnotations,
  } = useAnnotationsWithMetricOutputsTransport({
    metricId: metric?.id,
    projectIds: annotationProjectIds,
  })
  const isLoadingAnnotations =
    isInitialLoadingAnnotations ||
    (annotationProjectIds !== undefined && isFetchingAnnotations)

  // Fetch human review projects for filtering
  const { data: humanReviewProjects } = useHumanReviewProjectsTransport()
  const relevantProjects = useMemo(
    () =>
      humanReviewProjects?.filter((project) =>
        metric?.id ? project.linked_metric_ids.includes(metric.id) : false,
      ) ?? [],
    [humanReviewProjects, metric?.id],
  )
  const showHumanAgreementRate =
    isCompositeMetric &&
    canRevealHumanVsAiAgreement(
      selectedProjectId,
      relevantProjects,
      isFetchingAnnotations,
    )

  const updateAnnotationMutation = useUpdateReviewAnnotationTransport()
  const deleteAnnotationMutation = useDeleteReviewAnnotationTransport()

  // Fetch simulation output for transcript length display
  const { data: simulationOutput } = useSimulationOutputTransport(
    simulationId,
    { enabled: !!simulationId },
  )

  const allAnnotationsForSimulation = useMemo(() => {
    const all = annotationsData?.annotations[metric?.id || ""] ?? []
    return all.filter(
      (a: Annotation) => a.simulation_output_id === simulationId && !a.archived,
    )
  }, [annotationsData, metric?.id, simulationId])

  const annotations = allAnnotationsForSimulation

  // Show the same review the SimulationReviewTable stars (PRIMARY row, else
  // earliest by created_at), merged within that reviewer's own rows only —
  // never blending another reviewer's value or note in.
  const mergedAnnotation = useMemo(
    () => selectPrimaryReviewerMerged(annotations),
    [annotations],
  )

  const [isSettingPrimary, setIsSettingPrimary] = useState(false)
  const [archivingIds, setArchivingIds] = useState<Set<string>>(new Set())

  const handleSetPrimary = async (reviewId: string) => {
    const otherIds = allAnnotationsForSimulation
      .map((a) => a.id)
      .filter((id) => id !== reviewId)
    setIsSettingPrimary(true)
    try {
      await Promise.all([
        ...otherIds.map((id) =>
          updateAnnotationMutation.mutateAsync({
            annotationId: id,
            priority: "STANDARD",
          }),
        ),
        updateAnnotationMutation.mutateAsync({
          annotationId: reviewId,
          priority: "PRIMARY",
        }),
      ])
      invalidateReviewMetricTransport()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error"
      log.error("Failed to set primary annotation", message)
      toast.danger({
        title: "Failed to set primary review",
        description: "Something went wrong. Please try again.",
      })
    } finally {
      setIsSettingPrimary(false)
    }
  }

  const handleArchive = async (annotationId: string) => {
    setArchivingIds((prev) => new Set(prev).add(annotationId))
    try {
      await deleteAnnotationMutation.mutateAsync({
        annotationId,
      })
      invalidateReviewMetricTransport()
      toast.success({ title: "Annotation archived" })
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error"
      log.error("Failed to archive annotation", message)
      toast.danger({
        title: "Failed to archive annotation",
        description: "Something went wrong. Please try again.",
      })
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev)
        next.delete(annotationId)
        return next
      })
    }
  }

  const metricOutput =
    annotationsData?.metricOutputs[metric?.id || ""]?.[simulationId]

  // The annotations transport caches the metric_output that was linked at the
  // time of the annotation, so its individual_results can lag behind reality
  // after a re-evaluation. simulationOutput.metric_outputs is fetched fresh per
  // simulation and is a more accurate source for per-criterion breakdowns.
  const freshMetricOutput = metric?.id
    ? (simulationOutput?.metric_outputs?.[metric.id] ?? null)
    : null

  const aiCriteria = useMemo((): CriterionResult[] => {
    if (!isCompositeMetric) {
      return []
    }
    const fresh = extractCompositeResults(freshMetricOutput)
    if (fresh.length > 0) {
      return fresh
    }
    return extractCompositeResults(metricOutput)
  }, [isCompositeMetric, metricOutput, freshMetricOutput])

  // Criteria as they existed on the annotation-linked output — used to label
  // the human's positional votes. If the AI has since been re-evaluated with
  // a different ordering or set of criteria, we want the human's votes shown
  // under the names they were made against, not the current names.
  const annotationTimeAiCriteria = useMemo((): CriterionResult[] => {
    if (!isCompositeMetric) {
      return []
    }
    return extractCompositeResults(metricOutput)
  }, [isCompositeMetric, metricOutput])

  const transcript = useMemo(
    () => (simulationOutput?.transcript as Message[]) ?? [],
    [simulationOutput?.transcript],
  )
  const transcriptLength = transcript.length

  const highlightedMessages = useMemo(() => {
    if (!metric || !metricOutput) {
      return null
    }
    return (
      metric.getHighlightedMessages(
        metricOutput as unknown as RunMetricOutput,
      ) ?? null
    )
  }, [metric, metricOutput])

  const highlightedWords = useMemo(() => {
    if (!metric || !metricOutput || !transcript.length) {
      return null
    }
    return (
      metric.getHighlightedWords(
        metricOutput as unknown as RunMetricOutput,
        transcript,
      ) ?? null
    )
  }, [metric, metricOutput, transcript])

  const getAnnotationDisplayValue = (annotation: Annotation): string | null => {
    if (metric) {
      return metric.getHumanReviewDisplayValue({
        output_type: metric.output_type || "string",
        float_value: annotation.ground_truth_float_value,
        string_value: annotation.ground_truth_string_value,
        set_value: annotation.ground_truth_set_value,
        json_value:
          (annotation.ground_truth_json as Record<string, unknown>) ?? null,
      })
    }
    return annotation.ground_truth_string_value ?? null
  }

  const aiDisplayValue = metricOutput
    ? (metric?.getDisplayValueString(metricOutput) ?? null)
    : null

  const renderCompositeReviewSection = () => (
    <CompositeReviewSection
      metric={metric}
      metricOutput={metricOutput}
      freshMetricOutput={freshMetricOutput}
      aiCriteria={aiCriteria}
      annotationTimeAiCriteria={annotationTimeAiCriteria}
      mergedAnnotation={mergedAnnotation}
      annotations={annotations}
      isLoadingAnnotations={isLoadingAnnotations}
      getAnnotationDisplayValue={getAnnotationDisplayValue}
      handleSetPrimary={handleSetPrimary}
      isSettingPrimary={isSettingPrimary}
      handleArchive={handleArchive}
      archivingIds={archivingIds}
      expectedBehaviors={
        criteriaOverride ?? simulationOutput?.expected_behaviors ?? []
      }
      experimentResult={experimentResult}
      onRunExperiment={onRunExperiment}
      isRunningExperiment={isRunningExperiment}
      canRunEvaluations={canRunEvaluations}
      transcriptLength={transcriptLength}
      criteriaFirst={criteriaFirst}
      showHumanAgreementRate={showHumanAgreementRate}
      criteriaViewMode={criteriaViewMode}
    />
  )

  const renderReviewSection = () => {
    const outputVersionStatus =
      metricOutput && metric
        ? metric.getOutputVersionStatus(metricOutput)
        : null

    if (isCompositeMetric) {
      return renderCompositeReviewSection()
    }

    return (
      <div
        className={`grid ${canRunEvaluations ? "grid-cols-3" : "grid-cols-2"} gap-4 p-4 border-b border-coval-border shrink-0`}
      >
        {/* AI Review */}
        <div className="space-y-2 min-w-0">
          <h3 className="text-sm font-semibold text-coval-text">AI Review</h3>
          <HumanReviewAnnotationCard
            displayValue={aiDisplayValue}
            targetCondition={metric?.metric_metadata?.target_condition}
            date={formatDate(metricOutput?.created_at || null)}
            isPrimary={false}
            canSetPrimary={false}
            criteria={isCompositeMetric ? aiCriteria : []}
            explanation={metricOutput?.explanation || "No explanation"}
            footer={
              <>
                {metricOutput && metric && outputVersionStatus?.isStale && (
                  <div className="mb-2 space-y-1.5">
                    <MetricVersionTag
                      metric={metric}
                      metricOutput={metricOutput}
                      precomputedStatus={outputVersionStatus}
                      variant="full"
                    />
                    {metric.getVersionDiff(metricOutput).map((diff) => (
                      <div key={diff.field} className="text-xs space-y-0.5">
                        <p className="text-coval-text-muted font-medium">
                          {diff.field}
                        </p>
                        <p className="text-coval-text-muted whitespace-pre-wrap">
                          {diff.previous}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-coval-text-muted">
                  {transcriptLength} transcript items
                </p>
              </>
            }
          />
        </div>

        {/* Human Review */}
        <div className="space-y-2 min-w-0">
          <h3 className="text-sm font-semibold text-coval-text">
            Human Review
          </h3>
          {isLoadingAnnotations ? (
            <p className="text-sm text-coval-text-muted">Loading...</p>
          ) : mergedAnnotation ? (
            (() => {
              const annotation = mergedAnnotation
              const setValue = annotation.ground_truth_set_value
              const humanCriteria: CriterionResult[] =
                isCompositeMetric && setValue && setValue.length > 0
                  ? setValue.map((result, i) => ({
                      criterion:
                        aiCriteria[i]?.criterion ?? `Criterion ${i + 1}`,
                      result: isValidCriterionResult(result)
                        ? result
                        : CRITERION_VALUE.UNKNOWN,
                      explanation: "",
                    }))
                  : []
              return (
                <HumanReviewAnnotationCard
                  displayValue={getAnnotationDisplayValue(annotation)}
                  targetCondition={metric?.metric_metadata?.target_condition}
                  assignee={annotation.assignee}
                  date={formatDate(annotation.last_updated_at)}
                  isPrimary={annotation.priority === "PRIMARY"}
                  canSetPrimary={annotations.length > 1}
                  onSetPrimary={() => void handleSetPrimary(annotation.id)}
                  isSettingPrimary={isSettingPrimary}
                  onArchive={() => void handleArchive(annotation.id)}
                  isArchiving={archivingIds.has(annotation.id)}
                  criteria={humanCriteria}
                  explanation={
                    annotation.annotations?.custom_explanation ?? null
                  }
                />
              )
            })()
          ) : (
            <p className="text-sm text-coval-text">No annotation</p>
          )}
        </div>

        {/* Latest Experiment - only show for testable metric types */}
        {canRunEvaluations && (
          <div className="space-y-2 min-w-0">
            <h3 className="text-sm font-semibold text-coval-text">
              Most Recent AI Review
            </h3>
            {experimentResult ? (
              <>
                <ReviewBadge
                  value={experimentResult.value}
                  targetCondition={metric?.metric_metadata?.target_condition}
                  errorStatus={experimentResult.errorStatus}
                  errorMessage={experimentResult.errorMessage}
                />
                <p className="text-sm text-coval-text break-words">
                  {experimentResult.explanation}
                </p>
                <Tooltip content="Rerun experiment">
                  <button
                    onClick={() => {
                      trackEvent(
                        TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED,
                        { metric_id: metric?.id },
                      )
                      onRunExperiment()
                    }}
                    disabled={isRunningExperiment}
                    className="text-coval-text-muted hover:text-coval-text transition-colors disabled:opacity-50"
                    aria-label="Rerun experiment"
                  >
                    <RotateCw
                      className={`w-3.5 h-3.5 ${isRunningExperiment ? "animate-spin" : ""}`}
                    />
                  </button>
                </Tooltip>
                <p className="text-xs text-coval-text-muted">
                  Test your recent changes on this transcript
                </p>
              </>
            ) : (
              <>
                <Tooltip
                  content="Run experiment"
                  triggerDisabled={isRunningExperiment}
                >
                  <button
                    onClick={() => {
                      trackEvent(
                        TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED,
                        { metric_id: metric?.id },
                      )
                      onRunExperiment()
                    }}
                    disabled={isRunningExperiment}
                    className="text-coval-text-muted hover:text-coval-text transition-colors disabled:opacity-50"
                    aria-label="Run experiment"
                  >
                    <RotateCw
                      className={`w-4 h-4 ${isRunningExperiment ? "animate-spin" : ""}`}
                    />
                  </button>
                </Tooltip>
                <p className="text-xs text-coval-text-muted">
                  Test your recent changes on this transcript
                </p>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <TranscriptDetailView
      currentIndex={currentIndex}
      totalCount={totalCount}
      onNext={onNext}
      onPrevious={onPrevious}
      renderReviewSection={renderReviewSection}
      highlightedMessages={highlightedMessages}
      highlightedWords={highlightedWords}
      metricRecordForAnnotations={metric ?? undefined}
      bodyOverlay={bodyOverlay}
    />
  )
}
